/**
 * Which address the web app belongs on, and where to send a visitor who arrived on
 * another one — written once, here.
 *
 * The app is reachable on more hostnames than it lives on: the hosting provider's
 * default addresses cannot be detached from a site. Hosting's redirect rules apply to
 * every hostname of a site alike, so the server cannot redirect one and serve another.
 * src/boot.ts asks this module instead, before the app is loaded (openspec/specs/infra).
 * The bare domain and www are no longer among those hostnames: they belong to the public
 * site, which has a Hosting site of its own (site/). This module also says where that
 * site's terms and privacy policy are, for the pages that link them.
 *
 * A plain ES module with no dependencies, like csp.mjs: the unit tests run it with bare
 * `node`, and vite.config.ts uses it to refuse a malformed address at build time.
 */

const LOOPBACK = new Set(['localhost', '127.0.0.1', '[::1]']);

/**
 * The web address as a bare origin, or null when none is configured.
 * Throws on anything that is set but is not a bare https origin: a value with a path,
 * or without a scheme, would send every visitor somewhere that does not exist.
 *
 * @param {string | undefined | null} value  VITE_WEB_BASE_URL
 * @returns {string | null}
 */
export function checkedWebOrigin(value) {
  return checkedOrigin('VITE_WEB_BASE_URL', value);
}

/**
 * The public site's address (www.) as a bare origin, or null when none is configured.
 * The same check as the app's own: the sign-in screen and every share page link the
 * terms and the privacy policy there, and a malformed value links them nowhere.
 *
 * @param {string | undefined | null} value  VITE_SITE_URL
 * @returns {string | null}
 */
export function checkedSiteOrigin(value) {
  return checkedOrigin('VITE_SITE_URL', value);
}

/**
 * Where the terms and the privacy policy are, or null when no public site is configured
 * (local development). A malformed value is treated as not configured, as in
 * redirectTarget: the build has already refused it once.
 *
 * @param {string | undefined | null} siteUrl
 * @returns {{ terms: string, privacy: string } | null}
 */
export function legalLinks(siteUrl) {
  let origin;
  try {
    origin = checkedSiteOrigin(siteUrl);
  } catch {
    return null;
  }
  return origin ? { terms: `${origin}/terms`, privacy: `${origin}/privacy` } : null;
}

/**
 * The extension's download page on the configured public site, or null when none is
 * configured (openspec/changes/promote-extension). Same rules as legalLinks: the app
 * points at its own environment's site, and a malformed value links nowhere.
 *
 * @param {string | undefined | null} siteUrl
 * @returns {string | null}
 */
export function extensionLink(siteUrl) {
  let origin;
  try {
    origin = checkedSiteOrigin(siteUrl);
  } catch {
    return null;
  }
  return origin ? `${origin}/extension` : null;
}

function checkedOrigin(name, value) {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (!raw) return null;
  const fail = (why) => {
    throw new Error(`${name} ${JSON.stringify(raw)} ${why}`);
  };
  let url;
  try {
    url = new URL(raw);
  } catch {
    fail('is not an absolute URL');
  }
  if (url.protocol !== 'https:' && !(url.protocol === 'http:' && LOOPBACK.has(url.hostname))) {
    fail('must be https:// (http:// is allowed for loopback only)');
  }
  if (url.origin !== raw) fail(`must be a bare origin, exactly ${url.origin}`);
  return raw;
}

/**
 * Where a page on `location` should go, or null to stay.
 *
 * Null when no web address is configured (local development, the e2e build), when the
 * page is already on it, and on a loopback host. A configured value that is malformed
 * is treated as not configured: a redirect to nowhere is worse than no redirect, and
 * the build has already refused it once.
 *
 * The target is the configured origin with this page's path, query and fragment JOINED
 * on, never resolved against it: `new URL('//evil.example', origin)` leaves the site,
 * `origin + '//evil.example'` does not. Nothing in the request can choose the host.
 *
 * @param {string | undefined | null} webBaseUrl
 * @param {{ origin: string, hostname: string, pathname: string, search: string, hash: string }} location
 * @returns {string | null}
 */
export function redirectTarget(webBaseUrl, location) {
  let origin;
  try {
    origin = checkedWebOrigin(webBaseUrl);
  } catch {
    return null;
  }
  if (!origin) return null;
  if (location.origin === origin) return null;
  if (LOOPBACK.has(location.hostname)) return null;
  const path = location.pathname.startsWith('/') ? location.pathname : `/${location.pathname}`;
  return `${origin}${path}${location.search}${location.hash}`;
}
